import {
  BarChart3,
  BookOpen,
  ChevronRight,
  Home,
  LogOut,
  Navigation,
  Menu,
  MessageCircle,
  Radio,
  Settings,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { XOLogo } from "./XOLogo";

export type XOPage = "bridge" | "logbook" | "course" | "charts" | "signals" | "settings";

const navItems: Array<{
  id: XOPage | "ask";
  label: string;
  subtitle: string;
  Icon: typeof Home;
}> = [
  { id: "bridge", label: "Bridge", subtitle: "Operational briefing", Icon: Home },
  { id: "logbook", label: "Logbook", subtitle: "What XO saw", Icon: BookOpen },
  { id: "course", label: "Course", subtitle: "Seven-day plan", Icon: Navigation },
  { id: "charts", label: "Charts", subtitle: "Operational domains", Icon: BarChart3 },
  { id: "ask", label: "Ask XO", subtitle: "Operational questions", Icon: Sparkles },
  { id: "signals", label: "Signals", subtitle: "XO needs your judgement", Icon: Radio },
];

export const AppShell = ({
  activePage,
  askOpen,
  children,
  onAsk,
  onNavigate,
  onSignOut,
  pendingSignals,
}: {
  activePage: XOPage;
  askOpen: boolean;
  children: ReactNode;
  onAsk: () => void;
  onNavigate: (page: XOPage) => void;
  onSignOut: () => void;
  pendingSignals: number;
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!drawerOpen) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [drawerOpen]);

  const choose = (id: XOPage | "ask") => {
    setDrawerOpen(false);
    if (id === "ask") onAsk();
    else onNavigate(id);
  };

  return (
    <div className={`app-shell${askOpen ? " app-shell--panel-open" : ""}`}>
      <header className="topbar">
        <button className="logo-button" onClick={() => choose("bridge")} type="button">
          <XOLogo />
        </button>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {navItems.map(({ id, label }) => {
            if (id === "signals" && pendingSignals === 0) return null;
            const active = id === "ask" ? askOpen : activePage === id;
            return (
              <button
                aria-current={active ? "page" : undefined}
                className={active ? "is-active" : ""}
                key={id}
                onClick={() => choose(id)}
                type="button"
              >
                {label}
                {id === "signals" && pendingSignals > 0 ? (
                  <span className="signal-count">{pendingSignals}</span>
                ) : null}
              </button>
            );
          })}
        </nav>
        <div className="topbar__actions">
          {pendingSignals > 0 ? (
            <button className="mobile-signal" onClick={() => choose("signals")} type="button">
              Signals <span className="signal-count">{pendingSignals}</span>
            </button>
          ) : null}
          <button
            aria-label="Open navigation"
            className="menu-button"
            onClick={() => setDrawerOpen(true)}
            type="button"
          >
            <Menu />
          </button>
        </div>
      </header>

      <main className="app-main">{children}</main>

      <button className="ask-launcher" onClick={onAsk} type="button">
        <MessageCircle aria-hidden="true" />
        <span>Ask XO</span>
      </button>

      {drawerOpen ? (
        <div className="nav-drawer-layer" role="presentation" onMouseDown={() => setDrawerOpen(false)}>
          <aside
            aria-label="XO navigation"
            aria-modal="true"
            className="nav-drawer"
            onMouseDown={(event) => event.stopPropagation()}
            role="dialog"
          >
            <div className="nav-drawer__header">
              <XOLogo compact />
              <button aria-label="Close navigation" onClick={() => setDrawerOpen(false)} type="button">
                <X />
              </button>
            </div>
            <div className="nav-drawer__items">
              {navItems.map(({ id, label, subtitle, Icon }) => {
                if (id === "signals" && pendingSignals === 0) return null;
                const active = id === "ask" ? askOpen : activePage === id;
                return (
                  <button className={active ? "is-active" : ""} key={id} onClick={() => choose(id)} type="button">
                    <span className="drawer-icon"><Icon /></span>
                    <span><strong>{label}</strong><small>{subtitle}</small></span>
                    {id === "signals" ? <span className="signal-count">{pendingSignals}</span> : null}
                  </button>
                );
              })}
              <hr />
              <button onClick={() => choose("settings")} type="button">
                <span className="drawer-icon"><Settings /></span>
                <span><strong>Settings</strong><small>Connections and preferences</small></span>
              </button>
            </div>
            <div className="nav-drawer__footer">
              <div className="nav-drawer__account">
                <span className="avatar">A</span>
                <span><strong>Andrés</strong><small>Owner account</small></span>
                <ChevronRight />
              </div>
              <button className="signout-button" onClick={onSignOut} type="button">
                <LogOut /> Sign out
              </button>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
};
